// ============================================================
// analytics — fail-soft GA4 (gtag.js) runtime for the web app (Phase 34.2)
// ============================================================
//
// The actual GA4 wiring. Configuration (the enable-gate, the Measurement ID, the
// event-name catalog, the param sanitizer) lives in ./analyticsConfig (Phase 0);
// this module only loads gtag.js and forwards events.
//
// Hard rules (see blueprints/phase_34/ANALYTICS_CONTRACT.md):
//   • Off by default. Nothing loads and no network request is made unless
//     isAnalyticsEnabled() is true (flag + valid Measurement ID).
//   • Fail-soft. Every function is wrapped so a blocked/failed GA call (ad-blocker,
//     offline, GA outage) can never break a render, a login, or a tunnel.
//   • No PII. Event params pass through sanitizeEventParams(); page paths are sent
//     WITHOUT query strings (route tokens like ?token= never reach GA).

import {
  GA4_MEASUREMENT_ID,
  isAnalyticsEnabled,
  sanitizeEventParams,
} from './analyticsConfig';

let initialized = false;

// gtag must be defined synchronously (it just queues into dataLayer); the async
// <script> only flushes the queue once it loads. So events fired before the
// script arrives are not lost.
function ensureGtag() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    // Use the canonical gtag shim (pushes the `arguments` object, not an array).
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

// Inject gtag.js + configure GA4. Idempotent and gated. Call once at app start.
export function initAnalytics() {
  try {
    if (initialized) return;
    if (!isAnalyticsEnabled()) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    initialized = true; // set before the async load so a re-entrant call no-ops

    const gtag = ensureGtag();
    gtag('js', new Date());
    // send_page_view:false — SPA route changes drive page_view (trackPageView),
    // not gtag's automatic initial hit. anonymize_ip + ad-data redaction keep the
    // posture consistent with the Privacy Policy (Phase 0).
    gtag('config', GA4_MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
    });
    gtag('set', 'ads_data_redaction', true);

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  } catch {
    /* analytics must never break the app */
  }
}

// Manual SPA page_view. `path` should be a pathname WITHOUT query string.
export function trackPageView(path) {
  try {
    if (!isAnalyticsEnabled()) return;
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
    });
  } catch {
    /* fail-soft */
  }
}

// Named custom event. Params are sanitized (non-PII scalars only) before sending.
export function trackEvent(name, params = {}) {
  try {
    if (!name || !isAnalyticsEnabled()) return;
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    // gtag would otherwise attach the full page URL, query string included (which
    // can carry ?token=). Send origin + path only, as page views do (Phase 46).
    window.gtag('event', name, {
      ...sanitizeEventParams(params),
      page_location: window.location.origin + window.location.pathname,
    });
  } catch {
    /* fail-soft */
  }
}
